import {
    ApiPdpSellingPlan,
    ApiPdpSellingPlanGroup,
    DefaultPurchaseOption,
    isApiSellingPlanFixedAmountPriceAdjustment,
    isApiSellingPlanFixedPriceAdjustment,
    isApiSellingPlanPercentagePriceAdjustment,
} from '@storefronts/common-types'

import { convertPriceToCents } from './priceFormat'

/**
 * PDP-side selling plan helpers. These operate on the Admin-API-shaped
 * ApiPdpSellingPlanGroup data attached to landing page links (already
 * filtered server-side by subscription settings), unlike utils/subscriptions.ts
 * which operates on cart items and their Storefront API allocations.
 */

export function getPdpSellingPlans(
    sellingPlanGroups?: ApiPdpSellingPlanGroup[],
): ApiPdpSellingPlan[] {
    return (sellingPlanGroups ?? []).flatMap((group) => group.sellingPlans)
}

/**
 * A plan's price adjustments that apply from the first order.
 * orderCount of 0 means the adjustment applies to no orders.
 */
function applicableAdjustments(plan: ApiPdpSellingPlan) {
    return plan.priceAdjustments.filter(({ orderCount }) => orderCount !== 0)
}

/**
 * The subscription price (in cents) of a variant for a selling plan, from the
 * first applicable price adjustment. Plans with no applicable adjustment charge
 * the full variant price (common for subscription-only products that don't
 * discount). Null only when an adjustment exists but can't be computed.
 */
export function getPdpAdjustedPriceCents(
    variantPriceCents: number,
    plan: ApiPdpSellingPlan,
): number | null {
    const adjustment = applicableAdjustments(plan)[0]?.adjustmentValue
    if (!adjustment) {
        return variantPriceCents
    }
    if (isApiSellingPlanPercentagePriceAdjustment(adjustment)) {
        return Math.round(
            variantPriceCents * (1 - adjustment.adjustmentPercentage / 100),
        )
    }
    if (isApiSellingPlanFixedAmountPriceAdjustment(adjustment)) {
        const amountCents = convertPriceToCents(
            adjustment.adjustmentAmount.amount,
        )
        return amountCents === undefined
            ? null
            : Math.max(0, variantPriceCents - amountCents)
    }
    if (isApiSellingPlanFixedPriceAdjustment(adjustment)) {
        return convertPriceToCents(adjustment.price.amount) ?? null
    }
    return null
}

/**
 * Human-readable delivery frequency, e.g. "month" or "2 weeks". Some
 * subscription apps store opaque ids in `plan.options`, so the frequency is
 * derived from the plan name (the same field the cart displays), with a
 * redundant "Deliver every" / "Every" lead-in stripped so it reads cleanly
 * after the "Deliver every:" label.
 */
export function getPdpFrequencyLabel(plan: ApiPdpSellingPlan): string {
    const stripped = plan.name
        .replace(/^\s*deliver(?:y)?\s+every\s+/i, '')
        .replace(/^\s*every\s+/i, '')
        .trim()
    return stripped || plan.name
}

/**
 * Selling plans that can be offered for this variant. Plans with no price
 * adjustment are included (they charge full price). Plans whose adjustment
 * exists but can't be computed are excluded.
 */
export function getSubscribablePdpPlans(
    sellingPlanGroups: ApiPdpSellingPlanGroup[] | undefined,
    variantPriceCents: number | null,
): ApiPdpSellingPlan[] {
    if (variantPriceCents == null) {
        return []
    }
    return getPdpSellingPlans(sellingPlanGroups).filter(
        (plan) => getPdpAdjustedPriceCents(variantPriceCents, plan) != null,
    )
}

export type DefaultPdpSellingPlanIdParams = {
    sellingPlanGroups: ApiPdpSellingPlanGroup[] | undefined
    subscriptionBasePriceCents: number | null
    defaultPurchaseOption: DefaultPurchaseOption
    displaySubscriptionOptions: boolean
    /** When true, one-time isn't offered — always default to the first plan. */
    requiresSellingPlan?: boolean
}

/**
 * The selling plan a product should default to. Subscription-only products
 * always get the first subscribable plan. Otherwise honors the brand's
 * `defaultPurchaseOption`, gated by `displaySubscriptionOptions` so the
 * default stays inert while subscriptions can't be selected.
 */
export function getDefaultPdpSellingPlanId({
    sellingPlanGroups,
    subscriptionBasePriceCents,
    defaultPurchaseOption,
    displaySubscriptionOptions,
    requiresSellingPlan = false,
}: DefaultPdpSellingPlanIdParams): string | null {
    const shouldDefaultToSubscription =
        requiresSellingPlan ||
        (displaySubscriptionOptions && defaultPurchaseOption === 'SUBSCRIPTION')
    if (!shouldDefaultToSubscription) {
        return null
    }
    return (
        getSubscribablePdpPlans(
            sellingPlanGroups,
            subscriptionBasePriceCents,
        )[0]?.id ?? null
    )
}

export type AdjustedSelectedSellingPlanIdParams = {
    selectedSellingPlanId: string | null
    sellingPlanGroups: ApiPdpSellingPlanGroup[] | undefined
    subscriptionBasePriceCents: number | null
    requiresSellingPlan: boolean
    defaultSellingPlanId: string | null
}

/**
 * Next selling-plan selection when the current id is invalid for the variant,
 * or when a subscription-only product still has no plan selected. Returns the
 * current id when no adjustment is needed.
 */
export function getAdjustedSelectedSellingPlanId({
    selectedSellingPlanId,
    sellingPlanGroups,
    subscriptionBasePriceCents,
    requiresSellingPlan,
    defaultSellingPlanId,
}: AdjustedSelectedSellingPlanIdParams): string | null {
    const selectedPlanIsValid =
        selectedSellingPlanId != null &&
        getSubscribablePdpPlans(
            sellingPlanGroups,
            subscriptionBasePriceCents,
        ).some((plan) => plan.id === selectedSellingPlanId)
    if (
        !selectedPlanIsValid &&
        (selectedSellingPlanId != null || requiresSellingPlan)
    ) {
        return defaultSellingPlanId
    }
    return selectedSellingPlanId
}

/**
 * The whole-number percentage saved by subscribing to a plan for this variant,
 * e.g. 25 for a 25%-off plan. Null when there is no positive saving (so callers
 * can hide the "Save X%" pill). Shown per selected plan, so it reflects the
 * chosen frequency even when plans discount differently.
 */
export function getPdpSavingsPercent(
    variantPriceCents: number,
    plan: ApiPdpSellingPlan,
): number | null {
    const adjustedPrice = getPdpAdjustedPriceCents(variantPriceCents, plan)
    if (adjustedPrice == null || variantPriceCents <= 0) {
        return null
    }
    const percent = Math.round((1 - adjustedPrice / variantPriceCents) * 100)
    return percent > 0 ? percent : null
}

export type PdpDisplayPricing = {
    priceCents: number
    compareAtPriceCents: number | null
    discountPercentage: number | null
}

export type PdpDisplayPricingParams = {
    oneTimePriceCents: number
    listPriceCents: number | null
    oneTimeDiscountPercentage: number | null
    subscriptionBasePriceCents: number
    sellingPlanGroups: ApiPdpSellingPlanGroup[] | undefined
    selectedSellingPlanId: string | null
}

/**
 * The hero price, strikethrough, and discount badge for the product, reflecting
 * the selected plan. Shopify discounts the selling plan off the variant price
 * (not a promo layered on top), so a selected subscription is priced from
 * `subscriptionBasePriceCents` and strikes the true list price (or the variant
 * price when there's no list price). A subscription never borrows the one-time
 * promo's badge, since that promo doesn't apply to subscriptions.
 */
export function getPdpDisplayPricing({
    oneTimePriceCents,
    listPriceCents,
    oneTimeDiscountPercentage,
    subscriptionBasePriceCents,
    sellingPlanGroups,
    selectedSellingPlanId,
}: PdpDisplayPricingParams): PdpDisplayPricing {
    const selectedPlan = getSubscribablePdpPlans(
        sellingPlanGroups,
        subscriptionBasePriceCents,
    ).find((plan) => plan.id === selectedSellingPlanId)
    const subscriptionPriceCents = selectedPlan
        ? getPdpAdjustedPriceCents(subscriptionBasePriceCents, selectedPlan)
        : null

    const isSubscription = subscriptionPriceCents != null
    const priceCents = subscriptionPriceCents ?? oneTimePriceCents

    // Anchor to the true list price when it's above the shown price; otherwise a
    // subscription strikes the variant base it was discounted from.
    const hasListPrice = listPriceCents != null && listPriceCents > priceCents
    const compareAtPriceCents = hasListPrice
        ? listPriceCents
        : isSubscription
          ? subscriptionBasePriceCents
          : listPriceCents

    if (compareAtPriceCents != null && compareAtPriceCents > priceCents) {
        return {
            priceCents,
            compareAtPriceCents,
            discountPercentage: Math.round(
                (1 - priceCents / compareAtPriceCents) * 100,
            ),
        }
    }

    // No strikethrough: a subscription shows no badge (the one-time promo doesn't
    // apply to it); one-time keeps its own promo badge.
    return {
        priceCents,
        compareAtPriceCents,
        discountPercentage: isSubscription ? null : oneTimeDiscountPercentage,
    }
}

/**
 * Heading for the subscribe option: the configured override first, then
 * "Subscribe & Save" when every plan is discounted, else a generic fallback.
 * The savings percentage lives in the "Save X%" pill, not the heading.
 */
export function getPdpSubscriptionLabel(
    plans: ApiPdpSellingPlan[],
    variantPriceCents: number | null,
    subscriptionButtonText?: string | null,
): string {
    if (subscriptionButtonText) {
        return subscriptionButtonText
    }

    const adjustments = plans.flatMap((plan) =>
        applicableAdjustments(plan).map(
            ({ adjustmentValue }) => adjustmentValue,
        ),
    )

    const discounts = adjustments.map((adjustment) => {
        if (isApiSellingPlanPercentagePriceAdjustment(adjustment)) {
            return adjustment.adjustmentPercentage
        }
        if (isApiSellingPlanFixedAmountPriceAdjustment(adjustment)) {
            return convertPriceToCents(adjustment.adjustmentAmount.amount) ?? 0
        }
        if (
            isApiSellingPlanFixedPriceAdjustment(adjustment) &&
            variantPriceCents != null
        ) {
            const priceCents = convertPriceToCents(adjustment.price.amount)
            return priceCents === undefined ? 0 : variantPriceCents - priceCents
        }
        return 0
    })

    const allDiscounted =
        discounts.length > 0 && discounts.every((value) => value > 0)
    return allDiscounted ? 'Subscribe & Save' : 'Auto-Replenishment'
}
